// Default site content.
//
// This object is the single source of truth for everything shown on the
// consumer page. At runtime the page fetches the admin-edited version from the
// content API (see ../lib/content.js) and deep-merges it over these defaults,
// so the site always renders even if the API is unreachable and any brand-new
// fields fall back to the values below.

export const defaultContent = {
  brand: {
    name: 'WINDRISE STUDIOS',
  },
  images: {
    logo: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/84m389rl_WindRise_Logo.png',
    studio1: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/2ue9v5jh_Studio1.jpg',
    studio2: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/4jqddsvw_Studio2.jpg',
    studio3: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/lxqz7f6i_Studio3.jpg',
    studio4: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/tpz02ob9_Studio4.jpg',
    studio5: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/2ue9v5jh_Studio1.jpg',
    studio6: 'https://customer-assets.emergentagent.com/job_c46c6b17-7ae3-4107-99dd-36237b02d3c2/artifacts/2ue9v5jh_Studio1.jpg',
  },
  hero: {
    badge: 'Recording • Rehearsal • Gaming',
    titleLine1: 'WHERE MUSIC',
    titleLine2: 'MEETS MOMENTUM',
    subtitle:
      'A creative sanctuary for musicians and gamers. Professional recording facilities, jam spaces, and a cozy gaming lounge under one roof.',
  },
  services: {
    heading: 'Music Studio Services',
    subheading:
      'Professional-grade equipment and acoustically treated rooms for your creative journey',
    items: [
      {
        id: 1,
        category: 'music',
        title: 'Jam Sessions',
        price: '₹600 - ₹800',
        description: 'Collaborative space for bands and musicians',
        duration: 'Per hour',
        features: [
          '1-3 people: ₹600',
          '4-6 people: ₹700',
          '5-8 people: ₹800',
          'Additional instruments rental: ₹200',
          'Recording your jam session: ₹500 stereo track, ₹1000 multitrack',
        ],
      },
      {
        id: 2,
        category: 'music',
        title: 'Karaoke',
        price: '₹200 - ₹800',
        description: 'Record your karaoke session for 500rs',
        duration: 'Per hour',
        features: ['1-4 people: ₹500', '5-8 people: ₹800', 'More than 8 people: ₹200 per person'],
      },
      {
        id: 3,
        category: 'music',
        title: 'Recording',
        price: '₹800 - ₹1600',
        description: 'Jam and capture your session',
        duration: 'Per hour',
        features: ['Stereo Track: ₹800', 'Multitrack: ₹1200', 'Drum Recording: ₹1600'],
      },
      {
        id: 4,
        category: 'music',
        title: 'Music Production',
        price: '₹6,000 - ₹40,000',
        description: 'Professional music production',
        duration: 'Per track',
        features: ['Demo track: ₹6000', 'Standard tracks: ₹20000', 'Premium tracks: ₹40000'],
      },
      {
        id: 5,
        category: 'gaming',
        title: 'Gaming and Board Games',
        price: '₹100 - ₹250',
        description: 'Game room rentals',
        duration: 'Per person',
        features: ['Console Gaming: ₹150', 'Board Games: ₹100', 'Racing Simulator: ₹250 per 15 min'],
      },
      {
        id: 6,
        category: 'music',
        title: 'Mixing and Mastering',
        price: '₹10,000 - ₹25,000',
        description: 'Full-track professional mixing and mastering',
        duration: 'Per project',
        features: ['Basic Mixing: ₹10000', 'Standard Mixing: ₹15000', 'Premium Mixing: ₹25000'],
      },
    ],
  },
  about: {
    heading: 'Where Creativity Takes Shape',
    paragraphs: [
      'Windrise Studios is a modern creative hub designed for musicians and gamers. Our acoustically treated rooms, professional-grade equipment, and vibrant atmosphere make us the perfect spot for rehearsals, recordings, and creative hangouts.',
      "Whether you're a solo artist perfecting your craft, a band jamming together, or just looking for a cozy spot to game with friends - we've got you covered.",
    ],
    stats: [
      { value: '5+', label: 'Studio Services' },
      { value: '100+', label: 'Happy Artists' },
    ],
  },
  gaming: {
    heading: 'Chill. Play. Connect.',
    subheading: 'Take a break between sessions at our Nintendo Switch gaming lounge',
    setupTitle: 'Nintendo Switch Setup',
    setupSubtitle: '2 Gaming Stations',
    description:
      'Unwind between recording sessions or hang out with fellow creatives. Our gaming lounge features two Nintendo Switch setups with popular titles.',
    games: [
      { id: 1, name: 'Mario Kart', genre: 'Racing' },
      { id: 2, name: 'Super Smash Bros', genre: 'Fighting' },
      { id: 3, name: 'Legend of Zelda: Tears of the Kingdom', genre: 'Adventure' },
      { id: 4, name: 'Dragon Ball Fighterz', genre: 'Fighting' },
    ],
  },
  schedule: {
    heading: 'Available Slots & Studio Schedule',
    subheading: 'Check our real-time availability and plan your session accordingly',
    calendarSrc:
      'https://calendar.google.com/calendar/embed?src=windrisestudios%40gmail.com&ctz=Asia%2FKolkata&bgcolor=%23000000&color=%23f59e0b&mode=WEEK&showTitle=0&showNav=1&showDate=1&showPrint=0&showTabs=1&showCalendars=0',
  },
  contact: {
    heading: "Let's Create Something Amazing",
    paragraph:
      "Ready to book a session or have questions? Drop us a message and we'll get back to you soon. We handle scheduling manually via WhatsApp and Instagram DMs.",
    hours: 'Mon - Sun: 10 AM - 8 PM',
    whatsappNumber: '919741071144',
    whatsappDisplay: '+91 97410 71144',
    instagramHandle: '@windrisestudios',
    instagramUrl: 'https://www.instagram.com/windrisestudios/',
    locationAddress:
      'No. 64 Windrise Studios, Karthik nagar, Outer Ring Rd, LRDE Layout, Marathahalli, Bengaluru, Karnataka 560037',
    locationMapUrl:
      'https://maps.google.com/?q=No.64 Windrise Studios, Karthik nagar, Outer Ring Rd, LRDE Layout, Marathahalli, Bengaluru, Karnataka 560037',
  },
  footer: {
    copyright: '© 2025 Windrise Studios. All rights reserved.',
  },
};

// Backward-compatible named exports (the originals other code may import).
export const services = defaultContent.services.items;
export const gamingGames = defaultContent.gaming.games;
export const studioImages = defaultContent.images;
